import React, { useState, startTransition, useEffect } from "react"
import * as styles from "./deals.module.scss"
import { TitleType } from "../../../../types"
import { DealsPropsType, DealType } from "./types"
import { join_paths } from "../../../../utils/url"
import format from "date-fns/format"
import { classNames, deviceId } from "../../../../utils/viewport"
import useEmblaCarousel from "embla-carousel-react"
import useWindowResize from "../../../resize"

const Title: any = React.lazy(() => import("../../title"))
const Image: any = React.lazy(() => import("../../image"))
const Button: any = React.lazy(() => import("../../../button"))
const Carousel: any = React.lazy(() => import("../../../carousel"))
const Markdown: any = React.lazy(() => import("../../../markdown"))

export default function Deals({ data, ...props }: DealsPropsType) {
  const [title, _setTitle] = useState<TitleType>(data.title)
  const [deals, setDeals] = useState<DealType[] | null>(data.deals)
  const [currentSlide, setCurrentSlide] = useState<number>(0)
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    dragFree: false,
    loop: true,
    containScroll: "trimSnaps",
  })

  const handleViewportChange = (width: number | null) => {
    if (!width || !data.hiddenDeals) return
    startTransition(() =>
      setDeals(
        data.deals?.filter(
          (d: DealType) =>
            !d.display_on || d.display_on.includes(deviceId(width))
        ) as DealType[]
      )
    )
  }

  useWindowResize(handleViewportChange)

  const goToSlide = (index: number) => {
    if (emblaApi) {
      emblaApi.scrollTo(index)
      startTransition(() => setCurrentSlide(index))
    }
  }

  const onSelect = () => {
    if (!emblaApi) return
    const selectedSlide = emblaApi.selectedScrollSnap()
    if (selectedSlide != currentSlide) {
      startTransition(() => setCurrentSlide(selectedSlide))
    }
  }

  useEffect(() => {
    if (!emblaApi) return () => {}
    emblaApi.on("select", onSelect)
    onSelect() // Initial check
    return () => emblaApi.off("select", onSelect) // Clean up
  }, [emblaApi])

  return (
    !!deals && (
      <section className={`${styles.section} ${props.className ?? ""}`}>
        <Title {...title} />
        <Carousel
          id="deals-carousel"
          emblaRef={emblaRef}
          emblaApi={emblaApi}
          gap={20}
          showButtons={true}
          showDots={false}
          slidesPerView={1.05}
          slidesPerViewport={{
            1023: { slides: 1.1 },
          }}
          slides={deals?.map((deal: DealType, index: number) => (
            <div
              key={`deals-card${index}`}
              className={classNames({}, deal.display_on)}
            >
              <div className="deal-card">
                <div className="deal-img">
                  <a
                    href={join_paths(data.deals_root, deal.slug)}
                    title={`${deal.image_alt_text}`}
                  >
                    <Image {...deal} />
                  </a>
                </div>
                <div className="deal-definition">
                  <div>
                    <Title {...{ type: "title", title: deal.title }} />
                  </div>
                  <div className="description">
                    <Markdown>{deal.short_description}</Markdown>
                    <ul>
                      {!!deal.minimum_stay && (
                        <li>
                          {`Minimum ${deal.minimum_stay} night${
                            deal.minimum_stay > 1 ? "s" : ""
                          } stay required.`}
                        </li>
                      )}
                      <li>
                        Stay Dates:{" "}
                        {format(new Date(deal.start_date), "dd/MM/yyyy")}
                        {" - "}
                        {format(new Date(deal.end_date), "dd/MM/yyyy")}
                      </li>
                      {!!deal.display_end && (
                        <li>
                          Book By:{" "}
                          {format(new Date(deal.display_end), "d MMMM yyyy")}
                        </li>
                      )}
                    </ul>
                  </div>
                  <div className="link">
                    <a href={join_paths(data.deals_root, deal.slug)}>
                      <Button className={"btn1"}>VIEW OFFER</Button>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        />
        <ul className="deals-thumbs">
          {deals?.map((deal: DealType, index: number) => (
            <li
              key={`deals-thumb${index}`}
              className={`${currentSlide === index ? "active" : ""}`}
            >
              <Button onClick={() => goToSlide(index)}>
                <Title {...{ type: "title", title: deal.title }} />
              </Button>
            </li>
          ))}
        </ul>
      </section>
    )
  )
}
